import { useEffect, useMemo, useState } from "react";
import { ApiClient } from "../api/client";
import { useFixtures } from "../api/dataSource";
import {
  DISCLOSURES,
  canonicalDisclosureText,
  computeDisclosureHash,
} from "../lib/disclosures";

// First-run setup wizard — UX §4 + PRD §6.7.
//
// Phase 4 deliverable #4. Ten steps; the safety-critical surface is
// Step 1 (mandatory disclosures with hash + acknowledgment) plus the
// in-text acknowledgments inside Step 3 (risk capital) and Step 7
// (activity allocation), which PRD §6.7 calls out as the mandatory
// captures. Step 10 emits the system_activated event.
//
// Phase 4.5 D8: persistence wiring lands. The wizard now POSTs each
// milestone event to /api/v1/wizard/event on the running app
// process; the backend publishes onto the audit bus where the
// audit writer subscriber persists to the events table with hash
// chain linkage (TDD §3.2). Fixture mode (VITE_USE_FIXTURES=true)
// preserves the original console.info logging so the standalone
// dev experience continues to work without a backend.

const TOTAL_STEPS = 10;

type Props = {
  onActivate: () => void;
  onDefer: () => void;
  allowDefer?: boolean;
};

type WizardState = {
  step: number;
  ackDisclosures: boolean;
  disclosureHash: string | null;
  paperApiKey: string;
  paperApiSecret: string;
  paperConfirmPaper: boolean;
  investmentSetup: "skip" | "configure";
  investmentBroker: "alpaca" | "fidelity" | "schwab" | "vanguard";
  riskPerTradePct: number;
  dailyLossPct: number;
  weeklyLossPct: number;
  maxOpenPositions: number;
  maxSectorPct: number;
  maxSinglePositionPct: number;
  cashSleeveMinPct: number;
  capitalFloorPct: number;
  ackTradingRiskCapital: boolean;
  ackInvestmentDrawdown: boolean;
  polygonKey: string;
  pitProvider: "norgate" | "sharadar" | "none";
  aiMode: "enabled" | "indicator_only";
  anthropicKey: string;
  ackAIUnderstanding: boolean;
  newsProvider: "polygon" | "benzinga" | "tiingo" | "none";
  newsKey: string;
  enableResearchReports: boolean;
  ackNewsUnderstanding: boolean;
  allocIndex: number;
  allocTrading: number;
  allocInvestment: number;
  lessThanSixtyJustification: string;
  universeMode: "default" | "narrow";
  knownSectors: Record<string, boolean>;
  applyLenses: boolean;
  ackReadyToBegin: boolean;
};

const initialState = (): WizardState => ({
  step: 1,
  ackDisclosures: false,
  disclosureHash: null,
  paperApiKey: "",
  paperApiSecret: "",
  paperConfirmPaper: false,
  investmentSetup: "skip",
  investmentBroker: "fidelity",
  riskPerTradePct: 0.5,
  dailyLossPct: 2.0,
  weeklyLossPct: 5.0,
  maxOpenPositions: 20,
  maxSectorPct: 30,
  maxSinglePositionPct: 15,
  cashSleeveMinPct: 20,
  capitalFloorPct: 60,
  ackTradingRiskCapital: false,
  ackInvestmentDrawdown: false,
  polygonKey: "",
  pitProvider: "none",
  aiMode: "enabled",
  anthropicKey: "",
  ackAIUnderstanding: false,
  newsProvider: "polygon",
  newsKey: "",
  enableResearchReports: true,
  ackNewsUnderstanding: false,
  allocIndex: 70,
  allocTrading: 15,
  allocInvestment: 15,
  lessThanSixtyJustification: "",
  universeMode: "default",
  knownSectors: {
    Technology: true,
    Healthcare: true,
    Financials: true,
    "Consumer Discretionary": true,
    Energy: false,
    Materials: false,
    Industrials: false,
    "Consumer Staples": false,
    Utilities: false,
    "Real Estate": false,
    "Communication Services": false,
  },
  applyLenses: true,
  ackReadyToBegin: false,
});

export const FirstRunWizard = ({
  onActivate,
  onDefer,
  allowDefer = true,
}: Props) => {
  const [s, setS] = useState<WizardState>(initialState);
  const fixturesActive = useFixtures();
  const apiClient = useMemo(() => new ApiClient(), []);

  // Phase 4.5 D8: emit a wizard milestone event. In fixture
  // mode (no backend), fall back to console.info so the dev
  // experience remains unchanged. Failures during the API
  // POST are non-blocking — the wizard continues; the
  // operator can re-emit via a future re-confirmation flow.
  // The audit log NOT having an event for an activated system
  // would surface the next time the operator launches the
  // platform (no recent disclosures_acknowledged → re-prompt).
  const emitWizardEvent = async (
    eventType: "disclosures_acknowledged" | "system_activated",
    payload: Record<string, unknown>,
  ): Promise<void> => {
    if (fixturesActive) {
      // eslint-disable-next-line no-console
      console.info(`[wizard] ${eventType}`, payload);
      return;
    }
    try {
      await apiClient.postWizardEvent({
        event_type: eventType,
        payload,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        `[wizard] failed to persist ${eventType}; ` +
          "the platform will re-prompt next launch",
        e,
      );
    }
  };

  // Compute the disclosure hash once on mount; the value is included
  // in the would-be `disclosures_acknowledged` event payload.
  useEffect(() => {
    let cancelled = false;
    computeDisclosureHash().then((hash) => {
      if (!cancelled) {
        setS((curr) => ({ ...curr, disclosureHash: hash }));
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const set = <K extends keyof WizardState>(
    key: K,
    value: WizardState[K],
  ) => setS((curr) => ({ ...curr, [key]: value }));

  const allocSum = s.allocIndex + s.allocTrading + s.allocInvestment;
  const allocSums100 = allocSum === 100;
  const indexSubSixty = s.allocIndex < 60;
  const justificationOk =
    !indexSubSixty || s.lessThanSixtyJustification.trim().length >= 1;

  const canAdvance = useMemo(() => {
    switch (s.step) {
      case 1:
        return s.ackDisclosures && s.disclosureHash !== null;
      case 2:
        return (
          s.paperApiKey.length > 0 &&
          s.paperApiSecret.length > 0 &&
          s.paperConfirmPaper
        );
      case 3:
        return s.ackTradingRiskCapital && s.ackInvestmentDrawdown;
      case 4:
        return s.polygonKey.length > 0;
      case 5:
        return s.aiMode === "indicator_only" || (s.anthropicKey.length > 0 && s.ackAIUnderstanding);
      case 6:
        return s.newsProvider === "none" || (s.newsKey.length > 0 && s.ackNewsUnderstanding);
      case 7:
        return allocSums100 && justificationOk;
      case 8:
        return true;
      case 9:
        return true;
      case 10:
        return s.ackReadyToBegin;
      default:
        return false;
    }
  }, [s, allocSums100, justificationOk]);

  // Phase 5 (d) follow-up: persist API credentials to the OS
  // keychain via POST /api/v1/wizard/credentials BEFORE the
  // configure call. Empty strings are skipped server-side
  // (they mean "no change", NOT "clear"). If the credentials
  // POST fails, surface inline error and don't proceed.
  const persistWizardCredentials = async (): Promise<boolean> => {
    if (fixturesActive) {
      // eslint-disable-next-line no-console
      console.info("[wizard] (fixtures) credentials skipped");
      return true;
    }
    // Send only fields the operator actually filled in. The
    // backend treats blanks as "no change" anyway, but trimming
    // here avoids sending empty values over the wire.
    const body: Record<string, string> = {};
    if (s.polygonKey.trim()) body.polygon_api_key = s.polygonKey.trim();
    if (s.paperApiKey.trim()) body.alpaca_api_key = s.paperApiKey.trim();
    if (s.paperApiSecret.trim())
      body.alpaca_api_secret = s.paperApiSecret.trim();
    if (s.anthropicKey.trim())
      body.anthropic_api_key = s.anthropicKey.trim();
    if (s.newsKey.trim()) body.news_api_key = s.newsKey.trim();

    try {
      const resp = await apiClient.postWizardCredentials(body);
      // eslint-disable-next-line no-console
      console.info(
        "[wizard] credentials updated:",
        resp.updated,
        "broker_rebuilt:",
        resp.broker_rebuilt,
      );
      return true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[wizard] failed to persist credentials", e);
      return false;
    }
  };

  // Phase 5 D-Live: call /api/v1/wizard/configure with the full
  // wizard state so risk_config + the wizard_completed audit
  // event land before we dismiss the wizard.
  const persistWizardConfigure = async (): Promise<boolean> => {
    if (fixturesActive) {
      // Fixture mode has no backend.
      // eslint-disable-next-line no-console
      console.info("[wizard] (fixtures) configure skipped");
      return true;
    }
    try {
      await apiClient.postWizardConfigure({
        risk_per_trade_pct: String(s.riskPerTradePct),
        daily_loss_pct: String(s.dailyLossPct),
        weekly_loss_pct: String(s.weeklyLossPct),
        max_open_positions: s.maxOpenPositions,
        max_sector_pct: String(s.maxSectorPct),
        max_single_position_pct: String(s.maxSinglePositionPct),
        sectors_followed: Object.entries(s.knownSectors)
          .filter(([, on]) => on)
          .map(([name]) => name),
        universe_mode: s.universeMode,
        apply_lenses: s.applyLenses,
        ai_mode: s.aiMode,
        alloc_index: s.allocIndex,
        alloc_trading: s.allocTrading,
        alloc_investment: s.allocInvestment,
        investment_setup: s.investmentSetup,
        investment_broker: s.investmentBroker,
        pit_provider: s.pitProvider,
        news_provider: s.newsProvider,
        enable_research_reports: s.enableResearchReports,
      });
      return true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[wizard] failed to persist configure", e);
      return false;
    }
  };

  const [activating, setActivating] = useState<boolean>(false);
  const [activateError, setActivateError] = useState<string | null>(null);

  const next = () => {
    if (!canAdvance || activating) {
      return;
    }
    if (s.step === 1) {
      // Spec event payload (UX §4.1). Phase 4.5 D8: persist
      // via the app process's audit log.
      const payload = {
        hash: s.disclosureHash,
        text_length: canonicalDisclosureText().length,
      };
      void emitWizardEvent("disclosures_acknowledged", payload);
    }
    if (s.step === TOTAL_STEPS) {
      // Phase 5 (d) follow-up: write credentials to keychain
      // BEFORE configure so a failure here aborts cleanly
      // (configure / wizard_completed event would imply success
      // we don't actually have).
      setActivating(true);
      setActivateError(null);
      void persistWizardCredentials().then((credsOk) => {
        if (!credsOk) {
          setActivating(false);
          setActivateError(
            "Could not save your API credentials. Check the network and retry.",
          );
          return;
        }
        void persistWizardConfigure().then((ok) => {
        if (!ok) {
          setActivating(false);
          setActivateError(
            "Could not save your configuration. Check the network and retry.",
          );
          return;
        }
        const payload = {
          risk_per_trade_pct: s.riskPerTradePct,
          daily_loss_pct: s.dailyLossPct,
          weekly_loss_pct: s.weeklyLossPct,
          ai_mode: s.aiMode,
          alloc_index: s.allocIndex,
          alloc_trading: s.allocTrading,
          alloc_investment: s.allocInvestment,
        };
        void emitWizardEvent("system_activated", payload);
        // Phase 5 D-Live: persist the "activated" flag client-side
        // so the System scaffold's "Run first-run setup wizard"
        // button hides on subsequent visits. The DB still holds
        // the system_activated event in the audit chain — this
        // is the UI-side cache. (Future: a backend endpoint
        // /api/v1/system/activation-status will be the source
        // of truth across browsers.)
        try {
          window.localStorage.setItem(
            "tradeplatform_system_activated_at",
            new Date().toISOString(),
          );
        } catch {
          /* ignore — storage may be disabled in private mode */
        }
        setActivating(false);
        onActivate();
        });  // close persistWizardConfigure().then
      });  // close persistWizardCredentials().then
      return;
    }
    setS({ ...s, step: s.step + 1 });
  };

  const back = () => {
    if (s.step > 1) {
      setS({ ...s, step: s.step - 1 });
    }
  };

  return (
    <div className="tp-wizard-back" role="dialog" aria-modal="true">
      <div className="tp-wizard">
        <header className="tp-wizard-head">
          <div className="title">
            Setup — Step {s.step} of {TOTAL_STEPS}: {STEP_TITLES[s.step - 1]}
          </div>
          <ProgressBar step={s.step} total={TOTAL_STEPS} />
        </header>

        <div className="tp-wizard-body">
          {s.step === 1 && (
            <Step1Disclosures
              ack={s.ackDisclosures}
              setAck={(v) => set("ackDisclosures", v)}
              hash={s.disclosureHash}
            />
          )}
          {s.step === 2 && (
            <Step2BrokerAccounts state={s} set={set} />
          )}
          {s.step === 3 && (
            <Step3RiskConfig state={s} set={set} />
          )}
          {s.step === 4 && (
            <Step4DataProvider state={s} set={set} />
          )}
          {s.step === 5 && (
            <Step5AIService state={s} set={set} />
          )}
          {s.step === 6 && (
            <Step6NewsResearch state={s} set={set} />
          )}
          {s.step === 7 && (
            <Step7ActivityAllocation
              state={s}
              set={set}
              allocSum={allocSum}
              indexSubSixty={indexSubSixty}
            />
          )}
          {s.step === 8 && (
            <Step8Universe state={s} set={set} />
          )}
          {s.step === 9 && (
            <Step9Lenses state={s} set={set} />
          )}
          {s.step === 10 && (
            <Step10ReviewActivate state={s} set={set} />
          )}
        </div>

        <footer className="tp-wizard-foot">
          <div className="left">
            {allowDefer && s.step === 1 && (
              <button
                type="button"
                className="tp-btn tp-btn-ghost"
                onClick={onDefer}
              >
                Defer
              </button>
            )}
            {s.step > 1 && (
              <button
                type="button"
                className="tp-btn tp-btn-secondary"
                onClick={back}
              >
                Back
              </button>
            )}
          </div>
          <div className="right">
            {activateError && (
              <span
                role="alert"
                style={{
                  color: "var(--tp-bad, #c33)",
                  fontSize: 12,
                  marginRight: 12,
                }}
              >
                {activateError}
              </span>
            )}
            <button
              type="button"
              className="tp-btn tp-btn-primary"
              onClick={next}
              disabled={!canAdvance || activating}
            >
              {s.step === TOTAL_STEPS
                ? activating
                  ? "Saving…"
                  : "Activate"
                : "Next"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

const STEP_TITLES = [
  "Mandatory disclosures",
  "Broker accounts",
  "Risk limits per account",
  "Data providers",
  "AI service",
  "News and research reports",
  "Activity allocation",
  "Universe",
  "Industry lenses",
  "Review and activate",
];

// ---- Progress indicator -----------------------------------------------

const ProgressBar = ({ step, total }: { step: number; total: number }) => (
  <div className="tp-wizard-progress" aria-label={`Step ${step} of ${total}`}>
    {Array.from({ length: total }, (_, i) => i + 1).map((n) => (
      <div
        key={n}
        className={
          "dot " + (n < step ? "done" : n === step ? "active" : "pending")
        }
      />
    ))}
  </div>
);

// ---- Step 1: Mandatory Disclosures ------------------------------------

type StepProps = {
  state: WizardState;
  set: <K extends keyof WizardState>(key: K, value: WizardState[K]) => void;
};

const Step1Disclosures = ({
  ack,
  setAck,
  hash,
}: {
  ack: boolean;
  setAck: (v: boolean) => void;
  hash: string | null;
}) => (
  <>
    <p className="tp-wizard-lede">
      Before using this system, please read the following disclosures.
    </p>
    <div className="tp-disclosure-block">
      {DISCLOSURES.map((d) => (
        <div className="tp-disclosure" key={d.num}>
          <div className="num">{d.num}.</div>
          <div className="content">
            <div className="ti">{d.title}</div>
            <div className="bo">{d.body}</div>
          </div>
        </div>
      ))}
    </div>
    <div className="tp-ack" style={{ marginTop: 14 }}>
      <input
        id="ack-disclosures"
        type="checkbox"
        checked={ack}
        onChange={(e) => setAck(e.target.checked)}
      />
      <label htmlFor="ack-disclosures">
        I have read and understand each disclosure above.
      </label>
    </div>
    <div
      className="num"
      style={{
        marginTop: 12,
        fontSize: 11,
        color: "var(--tp-fg-4)",
        wordBreak: "break-all",
      }}
    >
      disclosure_text_sha256: {hash ?? "computing…"}
    </div>
  </>
);

// ---- Step 2: Broker Accounts -----------------------------------------

const Step2BrokerAccounts = ({ state, set }: StepProps) => (
  <>
    <p className="tp-wizard-lede">
      At minimum, configure trading paper. Live trading is locked at first-run;
      enable later after verification (PRD §7.1).
    </p>

    <section className="tp-wizard-section">
      <header>Trading paper (required)</header>
      <div className="tp-cfg-row">
        <span className="lbl">Broker</span>
        <span className="ctrl">
          <span className="num" style={{ fontWeight: 500 }}>
            Alpaca paper
          </span>
        </span>
      </div>
      <div className="tp-cfg-row">
        <label className="lbl" htmlFor="paper-key">
          API Key ID
        </label>
        <span className="ctrl">
          <input
            id="paper-key"
            type="text"
            placeholder="PK..."
            className="tp-input"
            value={state.paperApiKey}
            onChange={(e) => set("paperApiKey", e.target.value)}
          />
        </span>
      </div>
      <div className="tp-cfg-row">
        <label className="lbl" htmlFor="paper-secret">
          API Secret
        </label>
        <span className="ctrl">
          <input
            id="paper-secret"
            type="password"
            placeholder="••••••"
            className="tp-input"
            value={state.paperApiSecret}
            onChange={(e) => set("paperApiSecret", e.target.value)}
          />
        </span>
      </div>
      <div className="tp-ack">
        <input
          id="ack-paper-creds"
          type="checkbox"
          checked={state.paperConfirmPaper}
          onChange={(e) => set("paperConfirmPaper", e.target.checked)}
        />
        <label htmlFor="ack-paper-creds">
          I confirm these are PAPER credentials.
        </label>
      </div>
    </section>

    <section className="tp-wizard-section">
      <header>Investment account (optional)</header>
      <label className="tp-radio-row">
        <input
          type="radio"
          name="investment-setup"
          checked={state.investmentSetup === "skip"}
          onChange={() => set("investmentSetup", "skip")}
        />
        <span className="lbl">Skip for now</span>
      </label>
      <label className="tp-radio-row">
        <input
          type="radio"
          name="investment-setup"
          checked={state.investmentSetup === "configure"}
          onChange={() => set("investmentSetup", "configure")}
        />
        <span className="lbl">Configure investment account</span>
      </label>
      {state.investmentSetup === "configure" && (
        <div style={{ paddingLeft: 28, marginTop: 6 }}>
          {(["alpaca", "fidelity", "schwab", "vanguard"] as const).map(
            (b) => (
              <label className="tp-radio-row" key={b}>
                <input
                  type="radio"
                  name="investment-broker"
                  checked={state.investmentBroker === b}
                  onChange={() => set("investmentBroker", b)}
                />
                <span className="lbl">
                  {b === "alpaca" && "Same as trading (Alpaca)"}
                  {b === "fidelity" && "Fidelity"}
                  {b === "schwab" && "Schwab"}
                  {b === "vanguard" && "Vanguard (manual import only)"}
                </span>
              </label>
            ),
          )}
        </div>
      )}
    </section>
  </>
);

// ---- Step 3: Risk Configuration --------------------------------------

const Step3RiskConfig = ({ state, set }: StepProps) => (
  <>
    <p className="tp-wizard-lede">
      Risk limits are per-account. Live limits cannot be loosened relative to
      paper at setup time. You can tighten any limit later at any time;
      loosening goes through a 7-day cooling-off period.
    </p>

    <section className="tp-wizard-section">
      <header>Trading paper (defaults; tighten if desired)</header>
      <NumRow
        label="Risk per trade"
        unit="%"
        value={state.riskPerTradePct}
        step={0.1}
        onChange={(v) => set("riskPerTradePct", v)}
        helper="suggested 0.5–1.0%"
      />
      <NumRow
        label="Daily loss limit"
        unit="%"
        value={state.dailyLossPct}
        step={0.1}
        onChange={(v) => set("dailyLossPct", v)}
        helper="resets at session start"
      />
      <NumRow
        label="Weekly loss limit"
        unit="%"
        value={state.weeklyLossPct}
        step={0.1}
        onChange={(v) => set("weeklyLossPct", v)}
        helper="resets Monday session start"
      />
      <NumRow
        label="Max open positions"
        unit=""
        value={state.maxOpenPositions}
        step={1}
        onChange={(v) => set("maxOpenPositions", v)}
        helper="concurrent positions"
      />
      <NumRow
        label="Sector concentration"
        unit="%"
        value={state.maxSectorPct}
        step={1}
        onChange={(v) => set("maxSectorPct", v)}
        helper="max in one GICS sector"
      />
    </section>

    <section className="tp-wizard-section">
      <header>Investment (no daily/weekly limits, no stops)</header>
      <NumRow
        label="Max single position"
        unit="%"
        value={state.maxSinglePositionPct}
        step={1}
        onChange={(v) => set("maxSinglePositionPct", v)}
        helper="of investment allocation"
      />
      <NumRow
        label="Cash sleeve minimum"
        unit="%"
        value={state.cashSleeveMinPct}
        step={1}
        onChange={(v) => set("cashSleeveMinPct", v)}
      />
      <NumRow
        label="Capital floor"
        unit="%"
        value={state.capitalFloorPct}
        step={1}
        onChange={(v) => set("capitalFloorPct", v)}
        helper="of starting capital"
      />
    </section>

    <div className="tp-ack" style={{ marginTop: 12 }}>
      <input
        id="ack-trading-risk"
        type="checkbox"
        checked={state.ackTradingRiskCapital}
        onChange={(e) => set("ackTradingRiskCapital", e.target.checked)}
      />
      <label htmlFor="ack-trading-risk">
        I declare my trading allocation as risk capital I can afford to lose
        entirely without changing my circumstances materially.
      </label>
    </div>
    <div className="tp-ack">
      <input
        id="ack-invest-drawdown"
        type="checkbox"
        checked={state.ackInvestmentDrawdown}
        onChange={(e) => set("ackInvestmentDrawdown", e.target.checked)}
      />
      <label htmlFor="ack-invest-drawdown">
        I understand investment positions can drop 30-50% in a bear market and
        I will not panic-sell during such a drawdown.
      </label>
    </div>
  </>
);

const NumRow = ({
  label,
  unit,
  value,
  step,
  onChange,
  helper,
}: {
  label: string;
  unit: string;
  value: number;
  step: number;
  onChange: (v: number) => void;
  helper?: string;
}) => (
  <div className="tp-cfg-row">
    <span className="lbl">{label}</span>
    <span className="ctrl">
      <input
        type="number"
        className="tp-input num"
        step={step}
        value={value}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (Number.isFinite(v)) {
            onChange(v);
          }
        }}
        style={{ width: 110 }}
      />
      <span style={{ marginLeft: 8 }}>{unit}</span>
      {helper && (
        <span style={{ marginLeft: 12, fontSize: 11, color: "var(--tp-fg-3)" }}>
          {helper}
        </span>
      )}
    </span>
  </div>
);

// ---- Step 4: Data Provider -------------------------------------------

const Step4DataProvider = ({ state, set }: StepProps) => (
  <>
    <p className="tp-wizard-lede">
      Polygon.io is required for market data. Point-in-time index membership
      (Norgate / Sharadar) is recommended; without it, backtests inherit
      survivorship bias and cannot promote a strategy past the backtest gate
      (PRD §4.4.3).
    </p>
    <section className="tp-wizard-section">
      <header>Polygon.io API key (required)</header>
      <div className="tp-cfg-row">
        <label className="lbl" htmlFor="polygon-key">
          API key
        </label>
        <span className="ctrl">
          <input
            id="polygon-key"
            type="password"
            placeholder="••••••"
            className="tp-input"
            value={state.polygonKey}
            onChange={(e) => set("polygonKey", e.target.value)}
          />
        </span>
      </div>
    </section>
    <section className="tp-wizard-section">
      <header>Point-in-time index membership</header>
      {(["norgate", "sharadar", "none"] as const).map((p) => (
        <label className="tp-radio-row" key={p}>
          <input
            type="radio"
            name="pit-provider"
            checked={state.pitProvider === p}
            onChange={() => set("pitProvider", p)}
          />
          <span className="lbl">
            {p === "norgate" && "I have Norgate Data"}
            {p === "sharadar" && "I have Sharadar (Nasdaq Data Link)"}
            {p === "none" &&
              "None — backtest results will be marked as biased and cannot promote a strategy past the backtest gate."}
          </span>
        </label>
      ))}
      {state.pitProvider === "none" && (
        <div className="tp-banner warn" style={{ marginTop: 10 }}>
          <span className="icon">⚠</span>
          <span>
            Without point-in-time membership data, your backtests will use
            today's index constituents and inherit survivorship bias.
          </span>
        </div>
      )}
    </section>
  </>
);

// ---- Step 5: AI Service ----------------------------------------------

const Step5AIService = ({ state, set }: StepProps) => (
  <>
    <p className="tp-wizard-lede">
      The AI does the heavy analytical lifting: research, scoring, rationale,
      news synthesis, peer comparison, valuation context. You review and
      decide. AI runs in a separate process with no broker access (TDD §2.5).
      The user-still-approves-every-trade property is preserved.
    </p>
    <section className="tp-wizard-section">
      <header>Mode</header>
      <label className="tp-radio-row">
        <input
          type="radio"
          name="ai-mode"
          checked={state.aiMode === "enabled"}
          onChange={() => set("aiMode", "enabled")}
        />
        <span className="lbl">
          Enable AI workflow (recommended; this is how the product works)
        </span>
      </label>
      <label className="tp-radio-row">
        <input
          type="radio"
          name="ai-mode"
          checked={state.aiMode === "indicator_only"}
          onChange={() => set("aiMode", "indicator_only")}
        />
        <span className="lbl">Indicator-only mode (no AI; advanced)</span>
      </label>
    </section>

    {state.aiMode === "enabled" && (
      <>
        <section className="tp-wizard-section">
          <header>Anthropic API key</header>
          <div className="tp-cfg-row">
            <label className="lbl" htmlFor="anthropic-key">
              Key
            </label>
            <span className="ctrl">
              <input
                id="anthropic-key"
                type="password"
                placeholder="sk-ant-..."
                className="tp-input"
                value={state.anthropicKey}
                onChange={(e) => set("anthropicKey", e.target.value)}
              />
            </span>
          </div>
          <div className="tp-cfg-row">
            <span className="lbl">Pinned model version</span>
            <span className="ctrl num">claude-opus-4-7-20260101</span>
          </div>
        </section>
        <div className="tp-ack" style={{ marginTop: 12 }}>
          <input
            id="ack-ai"
            type="checkbox"
            checked={state.ackAIUnderstanding}
            onChange={(e) => set("ackAIUnderstanding", e.target.checked)}
          />
          <label htmlFor="ack-ai">
            I understand: AI scores are research input, never authority. Every
            trade still requires my explicit approval. There is no published
            evidence AI scoring improves outcomes.
          </label>
        </div>
      </>
    )}
  </>
);

// ---- Step 6: News and Research Reports -------------------------------

const Step6NewsResearch = ({ state, set }: StepProps) => (
  <>
    <p className="tp-wizard-lede">
      News inputs and AI research reports are enabled by default. They extend
      AI analysis with current context (news, fundamentals, macro) and
      on-demand long-form research reports for investment decisions.
    </p>
    <section className="tp-wizard-section">
      <header>News provider</header>
      {(
        [
          ["polygon", "Polygon news add-on (~$200/mo, recommended)"],
          ["benzinga", "Benzinga (~$199/mo, alternative)"],
          ["tiingo", "Tiingo (~$30/mo, cheap-tier ⚠)"],
          ["none", "None (skip news inputs)"],
        ] as const
      ).map(([id, label]) => (
        <label className="tp-radio-row" key={id}>
          <input
            type="radio"
            name="news-provider"
            checked={state.newsProvider === id}
            onChange={() => set("newsProvider", id)}
          />
          <span className="lbl">{label}</span>
        </label>
      ))}
    </section>

    {state.newsProvider === "tiingo" && (
      <div className="tp-banner warn">
        <span className="icon">⚠</span>
        <span>
          Your news provider is below the quality bar for live trading
          decisions. AI rationales will reflect the limitations of this data
          source.
        </span>
      </div>
    )}

    {state.newsProvider !== "none" && (
      <section className="tp-wizard-section">
        <header>News API key</header>
        <div className="tp-cfg-row">
          <label className="lbl" htmlFor="news-key">
            Key
          </label>
          <span className="ctrl">
            <input
              id="news-key"
              type="password"
              placeholder="••••••"
              className="tp-input"
              value={state.newsKey}
              onChange={(e) => set("newsKey", e.target.value)}
            />
          </span>
        </div>
        <label className="tp-toggle-row" style={{ marginTop: 8 }}>
          <input
            type="checkbox"
            checked={state.enableResearchReports}
            onChange={(e) =>
              set("enableResearchReports", e.target.checked)
            }
          />
          <span className="lbl">
            Enable AI research reports (cost $0.50–2.00 per report, 20/mo cap)
          </span>
        </label>
        <div className="tp-ack" style={{ marginTop: 12 }}>
          <input
            id="ack-news"
            type="checkbox"
            checked={state.ackNewsUnderstanding}
            onChange={(e) =>
              set("ackNewsUnderstanding", e.target.checked)
            }
          />
          <label htmlFor="ack-news">
            I understand: news inputs do not change AI authority — trade
            approval is still my decision. There is no published evidence
            news-augmented AI scoring produces better outcomes. Reports do not
            constitute investment advice; bear case is required reading.
          </label>
        </div>
      </section>
    )}
  </>
);

// ---- Step 7: Activity Allocation -------------------------------------

const Step7ActivityAllocation = ({
  state,
  set,
  allocSum,
  indexSubSixty,
}: StepProps & { allocSum: number; indexSubSixty: boolean }) => (
  <>
    <p className="tp-wizard-lede">
      How is your active capital split? Approximate is fine. Per PRD §1.6, the
      honest default for most retail capital is a low-cost broad-market index
      fund (VTI, VOO, VT). SPIVA reports show most active managers fail to
      beat their benchmark over 15+ year horizons.
    </p>
    <section className="tp-wizard-section">
      <PctRow
        label="Index funds outside this product"
        value={state.allocIndex}
        onChange={(v) => set("allocIndex", v)}
      />
      <PctRow
        label="Trading allocation in this product"
        value={state.allocTrading}
        onChange={(v) => set("allocTrading", v)}
      />
      <PctRow
        label="Active investment in this product"
        value={state.allocInvestment}
        onChange={(v) => set("allocInvestment", v)}
      />
      <div
        className="num"
        style={{
          textAlign: "right",
          paddingTop: 6,
          color: allocSum === 100 ? "var(--tp-ok)" : "var(--tp-bad)",
        }}
      >
        Total: {allocSum}%{" "}
        {allocSum !== 100 && <>(must sum to 100%)</>}
      </div>
    </section>

    {indexSubSixty && (
      <section className="tp-wizard-section" style={{ marginTop: 8 }}>
        <header style={{ color: "var(--tp-warn)" }}>
          ⚠ Less than 60% in passive index funds
        </header>
        <p style={{ fontSize: 13, color: "var(--tp-fg-1)" }}>
          This is a deliberate active-management bet. Why?
        </p>
        <textarea
          className="tp-input"
          style={{ width: "100%", minHeight: 80 }}
          placeholder="(1 sentence justification)"
          value={state.lessThanSixtyJustification}
          onChange={(e) => set("lessThanSixtyJustification", e.target.value)}
        />
      </section>
    )}
  </>
);

const PctRow = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) => (
  <div className="tp-cfg-row">
    <span className="lbl">{label}</span>
    <span className="ctrl">
      <input
        type="number"
        className="tp-input num"
        min={0}
        max={100}
        step={1}
        value={value}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (Number.isFinite(v)) {
            onChange(v);
          }
        }}
        style={{ width: 90 }}
      />
      <span style={{ marginLeft: 6 }}>%</span>
    </span>
  </div>
);

// ---- Step 8: Universe ------------------------------------------------

const Step8Universe = ({ state, set }: StepProps) => (
  <>
    <p className="tp-wizard-lede">
      Default: all US-listed equities and ETFs (~8,000 symbols) minus safety
      exclusions (penny stocks, illiquid names, OTC, leveraged ETFs,
      pre-merger SPACs, microcaps). Resulting universe ~3,200 symbols.
      Estimated monthly AI cost: $130–180.
    </p>
    <section className="tp-wizard-section">
      <header>Universe scope</header>
      <label className="tp-radio-row">
        <input
          type="radio"
          name="universe-mode"
          checked={state.universeMode === "default"}
          onChange={() => set("universeMode", "default")}
        />
        <span className="lbl">Accept default (broad universe)</span>
      </label>
      <label className="tp-radio-row">
        <input
          type="radio"
          name="universe-mode"
          checked={state.universeMode === "narrow"}
          onChange={() => set("universeMode", "narrow")}
        />
        <span className="lbl">
          Narrow now (recommended if you have a specific focus)
        </span>
      </label>
    </section>
    <section className="tp-wizard-section">
      <header>Sectors you currently follow</header>
      <p style={{ fontSize: 12, color: "var(--tp-fg-3)", marginTop: 0 }}>
        Unmarked sectors will be flagged at every approval modal for symbols
        in those sectors (informational, not blocking).
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 4,
          paddingTop: 6,
        }}
      >
        {Object.keys(state.knownSectors).map((sector) => (
          <label className="tp-toggle-row" key={sector}>
            <input
              type="checkbox"
              checked={state.knownSectors[sector] ?? false}
              onChange={(e) =>
                set("knownSectors", {
                  ...state.knownSectors,
                  [sector]: e.target.checked,
                })
              }
            />
            <span className="lbl">{sector}</span>
          </label>
        ))}
      </div>
    </section>
  </>
);

// ---- Step 9: Industry Lenses -----------------------------------------

const Step9Lenses = ({ state, set }: StepProps) => (
  <>
    <p className="tp-wizard-lede">
      Different industries are evaluated with different metrics. A bank is
      judged by NIM and credit quality; a biotech by pipeline depth and cash
      runway; a software company by ARR growth and net retention.
    </p>
    <section className="tp-wizard-section">
      <header>Auto-apply industry lenses</header>
      <label className="tp-radio-row">
        <input
          type="radio"
          name="lenses"
          checked={state.applyLenses}
          onChange={() => set("applyLenses", true)}
        />
        <span className="lbl">
          Apply industry lenses automatically (recommended). When AI scores a
          stock in a covered industry, lens-specific metrics and risk flags
          are added. 10 curated lenses ship.
        </span>
      </label>
      <label className="tp-radio-row">
        <input
          type="radio"
          name="lenses"
          checked={!state.applyLenses}
          onChange={() => set("applyLenses", false)}
        />
        <span className="lbl">
          Use generic scoring across all symbols. Lower cost, less
          industry-specific analysis.
        </span>
      </label>
    </section>
    <p
      style={{
        fontSize: 12,
        color: "var(--tp-fg-3)",
        marginTop: 8,
      }}
    >
      Curated lenses available: Banks, Biotech &amp; Pharma, Retail, Energy
      E&amp;P, Semiconductors, REITs, SaaS / Software, Insurance, Generic
      Growth, Generic Value. Custom lenses can be enabled later in Config →
      Lenses (advanced).
    </p>
  </>
);

// ---- Step 10: Review and Activate ------------------------------------

const Step10ReviewActivate = ({ state, set }: StepProps) => (
  <>
    <p className="tp-wizard-lede">
      Review your configuration. Activate writes the initial state and opens
      the workstation. You can change any of these later in Config.
    </p>
    <section className="tp-wizard-section">
      <header>Summary</header>
      <div className="tp-kv">
        <div className="k">Trading paper:</div>
        <div className="v num">
          {state.paperApiKey ? "configured" : "—"}
        </div>
        <div className="k">Investment:</div>
        <div className="v">
          {state.investmentSetup === "skip"
            ? "skipped"
            : state.investmentBroker}
        </div>
        <div className="k">Risk per trade:</div>
        <div className="v num">{state.riskPerTradePct}%</div>
        <div className="k">Daily loss limit:</div>
        <div className="v num">{state.dailyLossPct}%</div>
        <div className="k">Weekly loss limit:</div>
        <div className="v num">{state.weeklyLossPct}%</div>
        <div className="k">Data:</div>
        <div className="v num">
          Polygon.io · PIT {state.pitProvider}
        </div>
        <div className="k">AI:</div>
        <div className="v num">
          {state.aiMode === "enabled"
            ? "enabled · claude-opus-4-7-20260101"
            : "indicator-only"}
        </div>
        <div className="k">News provider:</div>
        <div className="v num">{state.newsProvider}</div>
        <div className="k">Activity:</div>
        <div className="v num">
          index {state.allocIndex}% · trading {state.allocTrading}% · invest{" "}
          {state.allocInvestment}%
        </div>
        <div className="k">Universe:</div>
        <div className="v num">{state.universeMode}</div>
        <div className="k">Lenses:</div>
        <div className="v num">{state.applyLenses ? "on" : "off"}</div>
      </div>
    </section>
    <div
      className="tp-banner info"
      style={{ marginTop: 12 }}
      role="status"
    >
      <span className="icon">ⓘ</span>
      <span>
        <b>Initial state on activate:</b> All v1 strategies are DISABLED — you
        will enable them via Strategies after running backtests. Live trading
        is LOCKED — paper only until verification phases complete (PRD §7.1).
      </span>
    </div>
    <div className="tp-ack" style={{ marginTop: 12 }}>
      <input
        id="ack-ready"
        type="checkbox"
        checked={state.ackReadyToBegin}
        onChange={(e) => set("ackReadyToBegin", e.target.checked)}
      />
      <label htmlFor="ack-ready">I am ready to begin paper trading.</label>
    </div>
  </>
);
